import React, { useMemo, useRef } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import { AnimatePresence, motion } from "framer-motion";
import { t } from "../i18n";
import {
  collapse,
  dialogSx as baseDialogSx,
  fieldSx,
  outlinedButtonSx,
  primaryButtonSx,
  secondaryButtonSx,
} from "../uiStyles";

const dialogSx = {
  ...baseDialogSx,
  "& .MuiDialog-paper": {
    ...baseDialogSx["& .MuiDialog-paper"],
    width: "min(640px, calc(100vw - 32px))",
  },
};

export default function AccountDialog({
  open,
  currentLocale,
  account,
  loading,
  onClose,
  onChange,
  onSave,
  onOpenChangePassword,
  dirty = false,
  onReset,
  onAvatarUpload,
  onAvatarRemove,
  currentTheme = "light",
  enforceTwoFactor = false,
}) {
  const fileRef = useRef(null);
  const isDark = currentTheme === "dark";
  const initials = useMemo(
    () =>
      (account?.displayName || account?.email || "U")
        .trim()
        .charAt(0)
        .toUpperCase(),
    [account?.displayName, account?.email],
  );

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      sx={dialogSx}
    >
      <DialogTitle sx={{ pb: 1.25, pr: 6 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t("settings.account", currentLocale)}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75 }}>
          {t("settings.accountText", currentLocale)}
        </Typography>
        <IconButton
          onClick={onClose}
          disabled={loading}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2.5}>
          <AnimatePresence initial={false}>
            {dirty && (
              <motion.div key="dirty" {...collapse}>
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 3,
                    bgcolor: "ui.dangerSoft",
                    border: "1px solid",
                    borderColor: "ui.dangerBorder",
                    color: "error.main",
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  {t("common.unsavedChanges", currentLocale)}
                </Box>
              </motion.div>
            )}
          </AnimatePresence>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(event) =>
                  onAvatarUpload?.(event.target.files?.[0] || null)
                }
              />
              <Tooltip
                title={
                  account?.avatarUrl
                    ? t("settings.removeAvatar", currentLocale)
                    : t("settings.uploadAvatar", currentLocale)
                }
                arrow
              >
                <span>
                  <IconButton
                    onClick={
                      account?.avatarUrl
                        ? onAvatarRemove
                        : () => fileRef.current?.click()
                    }
                    disabled={loading}
                    size="small"
                    sx={{
                      width: 72,
                      height: 72,
                      p: 0,
                      borderRadius: "50%",
                      position: "relative",
                      overflow: "hidden",
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "ui.avatar",
                      color: "#fff",
                      "& .avatar-hover-icon": {
                        opacity: 0,
                        transition: "opacity 0.15s ease",
                      },
                      "&:hover .avatar-hover-icon": { opacity: 1 },
                      "&:hover": { bgcolor: "primary.main" },
                    }}
                  >
                    {account?.avatarUrl ? (
                      <Avatar
                        src={account.avatarUrl}
                        sx={{
                          width: 72,
                          height: 72,
                          bgcolor: "transparent",
                          color: "#fff",
                          fontSize: 28,
                          fontWeight: 700,
                        }}
                      >
                        {initials}
                      </Avatar>
                    ) : (
                      <Avatar
                        sx={{
                          width: 72,
                          height: 72,
                          bgcolor: "transparent",
                          color: "#fff",
                          fontSize: 28,
                          fontWeight: 700,
                        }}
                      >
                        {initials}
                      </Avatar>
                    )}
                    <Box
                      className="avatar-hover-icon"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(17,24,39,0.35)",
                      }}
                    >
                      {account?.avatarUrl ? (
                        <DeleteOutlineIcon fontSize="small" />
                      ) : (
                        <PhotoCameraOutlinedIcon fontSize="small" />
                      )}
                    </Box>
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                {account?.displayName?.trim()
                  ? account.displayName
                  : account?.email || ""}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {account?.email || ""}
              </Typography>
            </Box>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              required
              label={currentLocale === "en" ? "Last name" : "Фамилия"}
              value={account?.lastName ?? ""}
              onChange={(event) => onChange?.("lastName", event.target.value)}
              fullWidth
              sx={fieldSx(isDark)}
            />
            <TextField
              required
              label={currentLocale === "en" ? "First name" : "Имя"}
              value={account?.firstName ?? ""}
              onChange={(event) => onChange?.("firstName", event.target.value)}
              fullWidth
              sx={fieldSx(isDark)}
            />
          </Stack>
          <TextField
            label={currentLocale === "en" ? "Middle name" : "Отчество"}
            value={account?.middleName ?? ""}
            onChange={(event) => onChange?.("middleName", event.target.value)}
            fullWidth
            sx={fieldSx(isDark)}
          />
          <TextField
            label={currentLocale === "en" ? "Position" : "Должность"}
            value={account?.position ?? ""}
            onChange={(event) => onChange?.("position", event.target.value)}
            fullWidth
            sx={fieldSx(isDark)}
          />
          <TextField
            multiline
            minRows={3}
            label={currentLocale === "en" ? "About" : "Описание"}
            value={account?.description ?? ""}
            onChange={(event) => onChange?.("description", event.target.value)}
            fullWidth
            sx={fieldSx(isDark)}
          />
          <TextField
            label={t("common.email", currentLocale)}
            value={account?.email || ""}
            fullWidth
            disabled
            sx={fieldSx(isDark)}
          />
          {!enforceTwoFactor && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                py: 0.25,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600 }}>
                  {currentLocale === "en"
                    ? "Two-factor authentication"
                    : "Двухфакторная авторизация"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mt: 0.5 }}
                >
                  {currentLocale === "en"
                    ? "Request an email verification code after entering your password"
                    : "Запрашивать код подтверждения по почте после ввода пароля"}
                </Typography>
              </Box>
              <Switch
                checked={!!account?.twoFactorEnabled}
                onChange={(event) =>
                  onChange?.("twoFactorEnabled", event.target.checked)
                }
                disabled={loading}
              />
            </Box>
          )}

          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              {t("settings.passwordSection", currentLocale)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 1.5 }}
            >
              {t("settings.passwordSectionText", currentLocale)}
            </Typography>
            <Button
              variant="outlined"
              onClick={onOpenChangePassword}
              sx={outlinedButtonSx}
            >
              {t("password.action", currentLocale)}
            </Button>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.25,
              pt: 1,
            }}
          >
            <Tooltip title={t("common.cancelHint", currentLocale)} arrow>
              <span>
                <Button
                  onClick={() => {
                    onReset?.();
                    onClose?.();
                  }}
                  disabled={loading}
                  sx={secondaryButtonSx}
                >
                  {t("common.cancel", currentLocale)}
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                dirty
                  ? t("common.saveHint", currentLocale)
                  : t("common.noChanges", currentLocale)
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  onClick={onSave}
                  disabled={
                    loading ||
                    !dirty ||
                    !account?.firstName?.trim() ||
                    !account?.lastName?.trim()
                  }
                  sx={primaryButtonSx}
                >
                  {loading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    t("common.save", currentLocale)
                  )}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
